import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';
import CartNudgeDialog from '../components/CartNudgeDialog';
import ExpertComparison from '../components/ExpertComparison';
import { Star, ShoppingCart, Truck, ShoppingBag, RotateCcw, ChevronRight, Heart, Check, Minus, Plus, Loader2, Package, Percent, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImage } from '../components/admin/ImageUpload';
import { computePricing, getUserTypeLabel, vatAmount } from '../utils/pricing';
import { useSettings } from '../context/SettingsContext';
import { useFavorites } from '../context/FavoritesContext';
import { usePricing } from '../context/PricingContext';
import { renderDescriptionHtml } from '../components/admin/RichTextField';
import SEO, { productSchema, breadcrumbSchema } from '../components/SEO';
import ReviewSection from '../components/ReviewSection';
import FlooringCalculator, { isFlooringProduct } from '../components/FlooringCalculator';
import ProTips from '../components/ProTips';

const ProductDetailPage = () => {
  const { id: rawId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem, items: cartItems } = useCart();
  const { user, effectiveUserType } = useAuth();
  const { settings } = useSettings();
  const { isFavorite, toggle } = useFavorites();
  const { resolveOverride, categories = [] } = usePricing() || {};
  const vatRate = settings?.vat_rate ?? 20;
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(null);
  const [cities, setCities] = useState([]);
  const [collectMode, setCollectMode] = useState(false);

  // Variant pre-selection: URL `?v=<id>` picks the variant on entry (used by
  // grid cards that materialise a variant into its own tile). Users can then
  // switch between variants; we keep the URL in sync so refresh/share works.
  const urlVariantId = searchParams.get('v');
  const [variantId, setVariantIdState] = useState(urlVariantId || null);

  // Default variant: if no ?v= and the product has one marked is_default, snap to it.
  // Must run before any early return per rules-of-hooks.
  useEffect(() => {
    if (!product || variantId) return;
    const def = (product.variants || []).find(v => v.is_default);
    if (def) setVariantIdState(def.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // The "effective product" is what discounts / pricing / add-to-cart consume.
  const effective = useMemo(() => {
    if (!product) return null;
    const selectedVariant = variantId ? (product.variants || []).find(v => v.id === variantId) : null;
    if (!selectedVariant) return product;
    return {
      ...product,
      name: selectedVariant.name,
      price: Number(selectedVariant.price),
      sku: selectedVariant.sku || product.sku,
      stock: selectedVariant.stock != null ? Number(selectedVariant.stock) : product.stock,
    };
  }, [product, variantId]);

  // Detect "slug-in-city" URL variant: strip the trailing "-in-<city>" so we still fetch
  // the same product, but retain the city context for localized copy + canonical.
  const cityMatch = cities.find(c => rawId?.endsWith(`-in-${c.slug}`));
  const productKey = cityMatch ? rawId.slice(0, rawId.length - `-in-${cityMatch.slug}`.length) : rawId;

  useEffect(() => {
    api.get('/cities').then(r => setCities(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!productKey) return;
    // Wait for cities to have loaded — a slug that ends with "-in-<city>" must be split
    // BEFORE we hit the API, otherwise we'll 404 on the raw URL.
    if (rawId?.includes('-in-') && cities.length === 0) return;
    setLoading(true);
    setSelectedImage(null);
    api.get(`/products/${productKey}`)
      .then(res => {
        setProduct(res.data);
        return Promise.all([
          api.get(`/products/related/${res.data.id}`, { params: { limit: 8 } }).catch(() => ({ data: [] })),
          api.get(`/products/accessories/${res.data.id}`).catch(() => ({ data: [] })),
        ]);
      })
      .then(([rel, acc]) => {
        setRelated((rel.data || []).slice(0, 8));
        setAccessories(acc.data || []);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [productKey, cities.length, rawId]);

  // Smart Retargeting — silently log the view when a signed-in user lands on
  // a product page. Anonymous browsers are skipped server-side. Fire-and-forget.
  useEffect(() => {
    if (!user || !product?.id) return;
    api.post('/track/product-view', { product_id: product.id }).catch(() => {});
  }, [user, product?.id]);

  if (loading) {
    return <div className="max-w-[1400px] mx-auto px-4 py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>;
  }
  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-[#12263A]">Product not found</h1>
        <Link to="/" className="text-[#F46A25] hover:underline mt-4 inline-block">Back to home</Link>
      </div>
    );
  }

  const userType = effectiveUserType || 'end_user';
  const override = resolveOverride ? resolveOverride(product) : null;

  // Variant metadata for the UI (selector + labels). `effective` was already
  // computed as a memo before the early-return above.
  const variants = product.variants || [];
  const selectedVariant = variantId ? variants.find(v => v.id === variantId) : null;

  const pickVariant = (vid) => {
    setVariantIdState(vid);
    // Keep URL in sync (preserves the city suffix + any other query params).
    const next = new URLSearchParams(searchParams);
    if (vid) next.set('v', vid); else next.delete('v');
    setSearchParams(next, { replace: true });
  };

  // "I will collect" — visible only when the admin has set a collection price
  // for the ACTIVE user tier on this product. Blank = hide toggle.
  const collectionPriceForTier = (() => {
    const raw = effective[`collection_price_${userType}`];
    return raw != null && raw !== '' && Number(raw) > 0 ? Number(raw) : 0;
  })();
  const hasCollectionPrice = collectionPriceForTier > 0;
  // Effective collect flag — the checkbox stays visually toggled if the admin
  // later removes the collection price, but no pricing/shipping actually flips.
  const collectActive = collectMode && hasCollectionPrice;

  const pricing = computePricing(effective, qty, userType, override, { collect: collectActive });
  // Total saving % vs the product's headline "was" price. Reflects bulk + tier
  // discount + any CRM override, so the badge stays consistent with the £ shown.
  const totalSavePct = effective.wasPrice && effective.wasPrice > pricing.unit
    ? Math.round((1 - pricing.unit / effective.wasPrice) * 100)
    : 0;
  // Trade rule: the standard bulk offer is a builder-only concession unless
  // a CRM override is set for this customer.
  const showBulkOffer = !!effective.bulk_qty && (userType === 'builder' || !!override);
  const bulkPricing = showBulkOffer ? computePricing(effective, effective.bulk_qty, userType, override) : null;
  const hasBulk = showBulkOffer && !!(effective.bulk_price);
  const bulkActive = hasBulk && qty >= effective.bulk_qty;

  const handleAdd = () => {
    if (!user) {
      toast.info('Please sign in to add items to your basket');
      navigate('/login?redirect=' + encodeURIComponent(`/product/${rawId}${variantId ? `?v=${variantId}` : ''}`));
      return;
    }
    addItem(effective, qty, variantId || null, { collect: collectActive });
    toast.success(`${qty} × ${effective.name} added to basket${collectActive ? ' (collect in store)' : ''}`);
    // Fire the cross-sell nudge if the admin has curated accessories for this
    // product. Skip the toast-only case when the list is empty so we don't
    // pop an empty modal at customers.
    if (accessories.length > 0) setNudgeOpen(true);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <SEO
        title={cityMatch ? `Buy ${product.name} in ${cityMatch.name}` : product.name}
        description={
          cityMatch
            ? `Buy ${product.name} in ${cityMatch.name}. Fast delivery across ${cityMatch.name}. Order before 2pm for next-day dispatch to all ${cityMatch.name} postcodes.`
            : ((product.description || '').replace(/<[^>]+>/g, '').slice(0, 155) || `${product.name} at Dr Tools. Buy online with trade pricing and next-day UK delivery.`)
        }
        // City pages must canonical to the master product URL — Google merges the signal.
        path={`/products/${product.slug || product.id}`}
        image={resolveImage(product.image)}
        type="product"
        jsonLd={[
          productSchema(product, { price: (product?.price || 0) }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            ...(categories || [])
              .filter(c => c.slug === product.category)
              .map(c => ({ name: c.name, path: `/category/${c.slug}` })),
            { name: product.name, path: `/products/${product.slug || product.id}` },
          ]),
        ]}
      />

      {cityMatch && (
        <div className="mb-4 bg-orange-50 border-l-4 border-[#F46A25] rounded-sm p-3" data-testid="city-banner">
          <div className="text-xs font-semibold text-[#F46A25] uppercase tracking-wide">Local delivery info</div>
          <div className="text-sm text-[#12263A] font-bold mt-0.5">Buy {product.name} in {cityMatch.name}</div>
          <p className="text-xs text-neutral-700 mt-1">
            Fast delivery across {cityMatch.name}. Order before 2 PM for next-day dispatch to all {cityMatch.name} postcodes.
          </p>
        </div>
      )}

      <nav className="flex items-center gap-2 text-xs text-neutral-600 mb-4 flex-wrap">
        <Link to="/" className="hover:text-[#F46A25]">Home</Link>
        {(() => {
          // Build breadcrumb from category tree so admins see friendly names,
          // not slugs like "new-8843".
          const bySlug = new Map(categories.map(c => [c.slug, c]));
          const byId = new Map(categories.map(c => [c.id, c]));
          const chain = [];
          let cur = bySlug.get(product.category);
          const seen = new Set();
          while (cur && !seen.has(cur.slug)) {
            seen.add(cur.slug);
            chain.push(cur);
            cur = cur.parent_id ? byId.get(cur.parent_id) : null;
          }
          chain.reverse();
          if (chain.length === 0) {
            return (
              <>
                <ChevronRight className="w-3 h-3" />
                <Link to={`/category/${product.category}`} className="hover:text-[#F46A25] capitalize">{product.category}</Link>
              </>
            );
          }
          return chain.map((c) => (
            <React.Fragment key={c.slug}>
              <ChevronRight className="w-3 h-3" />
              <Link
                to={`/category/${c.slug}`}
                data-testid={`breadcrumb-${c.slug}`}
                className="hover:text-[#F46A25]"
              >
                {c.name}
              </Link>
            </React.Fragment>
          ));
        })()}
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#12263A] font-semibold line-clamp-1">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8">
        <div className="bg-white border border-neutral-200 rounded-sm p-6">
          {(() => {
            const gallery = [product.image, ...(product.images || [])].filter(Boolean);
            const active = selectedImage || gallery[0];
            return (
              <>
                <div className="relative aspect-square bg-neutral-50 rounded-sm overflow-hidden">
                  <img src={resolveImage(active)} alt={product.name} width="800" height="800" className="w-full h-full object-cover" data-testid="pdp-main-image" />
                  {product.badge && (
                    <div className="absolute top-4 left-4 bg-[#F46A25] text-white text-xs font-bold px-3 py-1.5 rounded-sm">{product.badge}</div>
                  )}
                </div>
                {gallery.length > 1 && (
                  <div className="grid grid-cols-5 gap-2 mt-3">
                    {gallery.slice(0, 5).map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedImage(src)}
                        aria-label={`View image ${i + 1}`}
                        data-testid={`pdp-thumb-${i}`}
                        className={`aspect-square bg-neutral-50 border rounded-sm overflow-hidden transition-colors ${src === active ? 'border-[#F46A25] ring-1 ring-[#F46A25]' : 'border-neutral-200 hover:border-[#F46A25]'}`}
                      >
                        <img src={resolveImage(src)} alt={`View ${i + 1}`} width="120" height="120" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div>
          <div className="text-xs uppercase text-neutral-500 font-semibold mb-1">{product.brand}</div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#12263A] mb-3">{product.name}</h1>
          <div className="text-xs text-neutral-500 mb-3" data-testid="pdp-part-no">
            {product.part_no ? <>Dr Tools Part No: <span className="font-semibold text-[#12263A]">{product.part_no}</span></> : null}
          </div>

          {(product.size || product.others) && (
            <div className="mb-4 space-y-1.5" data-testid="pdp-quick-specs">
              {product.size && (
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="text-[11px] uppercase font-bold text-[#F46A25] w-16 flex-shrink-0">Size</span>
                  <span className="text-[#12263A] font-semibold" data-testid="pdp-size">{product.size}</span>
                </div>
              )}
              {product.others && (
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="text-[11px] uppercase font-bold text-[#F46A25] w-16 flex-shrink-0 mt-0.5">Details</span>
                  <div className="text-[#12263A] whitespace-pre-line leading-relaxed" data-testid="pdp-others">{product.others}</div>
                </div>
              )}
            </div>
          )}

          {/* Variant selector — only shown when the parent product has variants.
              Users can pre-select from the URL (?v=), then switch. Add-to-cart
              always uses the currently selected variant. */}
          {variants.length > 0 && (
            <div className="mb-4" data-testid="pdp-variant-selector">
              <div className="text-[11px] uppercase font-bold text-[#F46A25] mb-2">Choose pack size</div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => pickVariant(null)}
                  data-testid="pdp-variant-single"
                  className={`px-3 py-2 rounded-sm border text-sm font-semibold transition-all ${
                    !variantId
                      ? 'bg-[#12263A] text-white border-[#12263A]'
                      : 'bg-white text-[#12263A] border-neutral-300 hover:border-[#F46A25]'
                  }`}
                >
                  Single unit — £{Number(product.price).toFixed(2)}
                </button>
                {variants.map(v => (
                  <button
                    key={v.id}
                    onClick={() => pickVariant(v.id)}
                    data-testid={`pdp-variant-option-${v.id}`}
                    className={`px-3 py-2 rounded-sm border text-sm font-semibold transition-all text-left ${
                      variantId === v.id
                        ? 'bg-[#F46A25] text-white border-[#F46A25]'
                        : 'bg-white text-[#12263A] border-neutral-300 hover:border-[#F46A25]'
                    }`}
                  >
                    <div>{v.name}</div>
                    <div className={`text-xs ${variantId === v.id ? 'text-white/90' : 'text-neutral-500'}`}>
                      £{Number(v.price).toFixed(2)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <div className="flex">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className={`w-4 h-4 ${i <= Math.round(product.rating) ? 'fill-[#F46A25] text-[#F46A25]' : 'text-neutral-300'}`} />
              ))}
            </div>
            <span className="text-sm font-semibold text-[#12263A]">{product.rating}</span>
            <span className="text-sm text-neutral-600">({product.reviews} reviews)</span>
          </div>

          <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-4 mb-4">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-3xl font-black text-[#12263A]">£{pricing.unit.toFixed(2)}</span>
              {product.wasPrice && product.wasPrice > pricing.unit && (
                <>
                  <span className="text-lg text-neutral-500 line-through">£{product.wasPrice.toFixed(2)}</span>
                  <span className="bg-[#F46A25] text-white text-xs font-bold px-2 py-0.5 rounded-sm">Save {totalSavePct}%</span>
                </>
              )}
              <span className="text-xs text-neutral-500">per {isFlooringProduct(product) ? 'box' : (product.unit || 'unit')}</span>
            </div>
            {isFlooringProduct(product) && (
              <div className="text-sm text-neutral-600 mt-1">
                <b>£{(pricing.unit / (product.coverage_per_box_sqm || 1)).toFixed(2)}</b> per sqm · covers <b>{Number(product.coverage_per_box_sqm).toFixed(2)} sqm</b> per box
              </div>
            )}
            {pricing.discount > 0 && (
              <div className="mt-2 flex items-center gap-1.5 text-sm text-green-700 bg-green-50 border border-green-200 rounded-sm px-2 py-1 w-fit">
                <Percent className="w-3.5 h-3.5" />
                <span className="font-semibold">{getUserTypeLabel(userType)} price</span>
                <span className="text-xs">- {pricing.discount}% off applied</span>
              </div>
            )}
            <div className="flex items-center gap-2 mt-2 text-sm text-green-700">
              <Check className="w-4 h-4" />
              <span className="font-semibold">In stock</span>
              <span className="text-neutral-600">- {effective.stock} available</span>
            </div>
          </div>

          {hasBulk && (
            <div className={`mb-4 rounded-sm border-2 overflow-hidden ${bulkActive ? 'border-[#F46A25] bg-orange-50' : 'border-orange-200 bg-gradient-to-r from-[#FFF6EF] to-white'}`}>
              <div className="bg-[#F46A25] text-white px-4 py-1.5 flex items-center gap-2">
                <Package className="w-4 h-4" />
                <span className="font-black text-sm uppercase">Bulk buy offer</span>
                {bulkActive && <span className="ml-auto text-xs bg-white text-[#F46A25] px-2 py-0.5 rounded-sm font-bold">ACTIVE</span>}
              </div>
              <div className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-sm text-[#12263A]">Buy <span className="font-black text-lg">{product.bulk_qty}+</span> and save</div>
                  <div className="text-2xl font-black text-[#F46A25]">£{bulkPricing.unit.toFixed(2)} <span className="text-sm text-[#12263A] font-normal">each</span></div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-neutral-600">Save vs single</div>
                  <div className="text-lg font-bold text-green-700">£{(pricing.base - bulkPricing.unit).toFixed(2)}/{product.unit || 'unit'}</div>
                </div>
                {!bulkActive && (
                  <button onClick={() => setQty(product.bulk_qty)} className="bg-[#12263A] hover:bg-[#F46A25] text-white text-xs font-bold px-3 py-2 rounded-sm w-full transition-colors">
                    Get bulk deal - set qty to {product.bulk_qty}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-semibold text-[#12263A]">Quantity:</span>
            <div className="flex items-center border border-neutral-300 rounded-sm">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="Decrease" className="px-3 py-2 hover:bg-neutral-100"><Minus className="w-4 h-4" /></button>
              <span className="px-4 py-2 min-w-[3rem] text-center font-semibold">{qty}</span>
              <button onClick={() => setQty(q => Math.min(effective.stock, q + 1))} aria-label="Increase" className="px-3 py-2 hover:bg-neutral-100"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {hasCollectionPrice && (
            <label
              className={`mb-4 flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition-colors ${collectMode ? 'bg-orange-50 border-[#F46A25]' : 'bg-white border-neutral-300 hover:border-[#F46A25]'}`}
              data-testid="pdp-collect-toggle"
            >
              <input
                type="checkbox"
                checked={collectMode}
                onChange={e => setCollectMode(e.target.checked)}
                data-testid="pdp-collect-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#F46A25]"
              />
              <div className="flex-1">
                <div className="text-sm font-bold text-[#12263A] flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 text-[#F46A25]" /> I will collect this order
                </div>
                <div className="text-[11px] text-neutral-600 mt-0.5">
                  Collection price: <span className="font-semibold">£{collectionPriceForTier.toFixed(2)}</span> per {product.unit || 'unit'}
                </div>
              </div>
            </label>
          )}

          <div className="flex gap-2 mb-6">
            <button onClick={handleAdd} data-testid="pdp-add-to-basket-btn" className="flex-1 bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-3 rounded-sm flex items-center justify-center gap-2 transition-colors">
              <ShoppingCart className="w-5 h-5" /> Add to basket
            </button>
            <button
              onClick={async () => {
                const res = await toggle(product);
                if (res?.requiresLogin) navigate('/login?redirect=' + encodeURIComponent(`/product/${rawId}`));
              }}
              aria-label={isFavorite(product.id) ? 'Remove from list' : 'Save to list'}
              data-testid="product-detail-favorite-btn"
              className={`px-4 border rounded-sm transition-colors ${isFavorite(product.id) ? 'bg-[#F46A25] border-[#F46A25] text-white hover:bg-[#e05a15]' : 'border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25]'}`}
            >
              <Heart className={`w-5 h-5 ${isFavorite(product.id) ? 'fill-white' : ''}`} />
            </button>
          </div>

          {isFlooringProduct(product) && (
            <FlooringCalculator
              product={product}
              onApplyQty={(boxes) => {
                setQty(Math.min(Math.max(1, boxes), effective.stock));
                toast.info(`Quantity set to ${boxes}. Review it and press Add to basket when ready.`);
              }}
            />
          )}

          {product.datasheet_url && (
            <a
              href={resolveImage(product.datasheet_url)}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="product-datasheet-link"
              className="mb-6 flex items-center gap-3 border border-[#F46A25]/30 bg-[#FFF6EF] hover:bg-[#F46A25] hover:text-white transition-colors rounded-sm px-4 py-3 group"
            >
              <FileText className="w-5 h-5 text-[#F46A25] group-hover:text-white flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-bold text-[#12263A] group-hover:text-white">Download Data Sheet</div>
                <div className="text-[11px] text-neutral-600 group-hover:text-white/90">PDF · full technical specification</div>
              </div>
              <Download className="w-4 h-4 text-[#F46A25] group-hover:text-white" />
            </a>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6">
            {(() => {
              // Per-product shipping override display. Priority: free_delivery_qty > individual cost > global rule.
              const freeQty = Number(product.free_delivery_qty || 0);
              const perUnit = Number(product.individual_delivery_cost || 0);
              let deliveryLabel = 'Free over £75';
              if (freeQty > 0 && perUnit > 0) deliveryLabel = `£${perUnit.toFixed(2)}/unit · Free from ${freeQty}+`;
              else if (freeQty > 0) deliveryLabel = `Free from ${freeQty}+`;
              else if (perUnit > 0) deliveryLabel = `£${perUnit.toFixed(2)} per unit`;
              return (
                <div
                  className="border border-neutral-200 rounded-sm p-3 flex items-start gap-2"
                  data-testid="pdp-shipping-info"
                >
                  <Truck className="w-5 h-5 text-[#F46A25] mt-0.5" />
                  <div>
                    <div className="text-xs font-bold">Delivery</div>
                    <div className="text-[11px] text-neutral-600" data-testid="pdp-shipping-label">{deliveryLabel}</div>
                  </div>
                </div>
              );
            })()}
            <div className="border border-neutral-200 rounded-sm p-3 flex items-start gap-2">
              <ShoppingBag className="w-5 h-5 text-[#F46A25] mt-0.5" />
              <div><div className="text-xs font-bold">Click + Collect</div><div className="text-[11px] text-neutral-600">Ready in 1 hour</div></div>
            </div>
            <div className="border border-neutral-200 rounded-sm p-3 flex items-start gap-2">
              <RotateCcw className="w-5 h-5 text-[#F46A25] mt-0.5" />
              <div><div className="text-xs font-bold">Returns</div><div className="text-[11px] text-neutral-600">90-day policy</div></div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 bg-white border border-neutral-200 rounded-sm">
        <div className="flex border-b border-neutral-200">
          {['description', 'specifications', 'reviews'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-6 py-3 text-sm font-semibold capitalize transition-colors ${tab === t ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-600 hover:text-[#12263A]'}`}>{t}</button>
          ))}
        </div>
        <div className="p-6 text-sm text-neutral-700 leading-relaxed">
          {tab === 'description' && (
            <div
              data-testid="pdp-description"
              className="prose prose-sm max-w-none text-[#12263A] leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              dangerouslySetInnerHTML={renderDescriptionHtml(product.description)}
            />
          )}
          {tab === 'specifications' && (
            <table className="w-full text-sm" data-testid="product-specifications-table">
              <tbody>
                <tr className="border-b border-neutral-100"><td className="py-2 font-semibold w-1/3">Brand</td><td>{product.brand}</td></tr>
                <tr className="border-b border-neutral-100"><td className="py-2 font-semibold">Dr Tools Part No</td><td>{product.part_no || <span className="text-neutral-400">—</span>}</td></tr>
                <tr className="border-b border-neutral-100"><td className="py-2 font-semibold">Category</td><td>{(categories.find(c => c.slug === product.category)?.name) || product.category}</td></tr>
                <tr className="border-b border-neutral-100"><td className="py-2 font-semibold">Size</td><td>{product.size || <span className="text-neutral-400">—</span>}</td></tr>
                <tr className="border-b border-neutral-100"><td className="py-2 font-semibold align-top">Details</td><td className="whitespace-pre-line">{product.others || <span className="text-neutral-400">—</span>}</td></tr>
                {(product.weight_kg || product.length_cm || product.width_cm || product.height_cm) && (
                  <tr className="border-b border-neutral-100"><td className="py-2 font-semibold align-top">Weight &amp; dimensions</td><td data-testid="pdp-spec-dimensions">
                    {product.weight_kg ? <span>Weight: <b>{Number(product.weight_kg).toFixed(2)} kg</b></span> : null}
                    {(product.length_cm || product.width_cm || product.height_cm) && (
                      <span>{product.weight_kg ? ' · ' : ''}Size: <b>{[product.length_cm, product.width_cm, product.height_cm].map(d => d ? `${Number(d).toFixed(1)}` : '—').join(' × ')} cm</b></span>
                    )}
                  </td></tr>
                )}
                <tr><td className="py-2 font-semibold">Stock</td><td>{product.stock} units</td></tr>
              </tbody>
            </table>
          )}
          {tab === 'reviews' && (
            <ProductReviews product={product} />
          )}
        </div>
      </div>

      {/* EEAT — Professional Usage Tips (LLM-generated on new products) */}
      <ProTips tips={product.pro_tips} productName={product.name} />

      {/* Customer reviews with photo uploads + moderation */}
      <ReviewSection product={product} />

      {accessories.length > 0 && (
        <CompleteProjectSection
          accessories={accessories}
          brand={product.brand}
          inCartIds={new Set((cartItems || []).map(i => i.id))}
          onAdd={(p) => addItem(p, 1, null, {})}
        />
      )}

      {/* Expert Comparison — auto-generated for every product using product-
          level override → category default → universal fallback. Never hides. */}
      <ExpertComparison product={product} brandName={product.brand || 'Dr Tools UK'} />

      {related.length > 0 && (
        <section className="mt-10" data-testid="related-products">
          <h2 className="text-xl md:text-2xl font-black text-[#12263A] mb-1">Related products</h2>
          <p className="text-sm text-neutral-600 mb-4">More from {product.brand} and {product.category?.replace(/-/g, ' ')}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {related.map(p => <ProductCard key={`${p.id}-${p.variant_id || 'base'}`} product={p} />)}
          </div>
        </section>
      )}

      <CartNudgeDialog
        open={nudgeOpen}
        onClose={() => setNudgeOpen(false)}
        parentProduct={effective}
        accessories={accessories}
      />
    </div>
  );
};

export default ProductDetailPage;

/**
 * "Complete your project" — admin-curated cross-sell strip rendered above
 * the auto-derived "Related products". Each card exposes a quick add-to-cart
 * button that fires the parent's `onAdd` handler and toasts the addition.
 * Rendered only when the accessories array is non-empty (parent guards).
 */
const CompleteProjectSection = ({ accessories, brand, inCartIds, onAdd }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(null);

  const handleAdd = async (p) => {
    if (!user) {
      toast.info('Please sign in to add items to your basket');
      navigate('/login');
      return;
    }
    setAdding(p.id);
    try {
      await onAdd(p);
      toast.success(`${p.name} added to basket`);
    } finally {
      setAdding(null);
    }
  };

  return (
    <section className="mt-10 border border-orange-200 bg-orange-50/30 rounded-sm p-5" data-testid="complete-your-project">
      <div className="mb-4">
        <span className="inline-block bg-[#F46A25] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-sm mb-1 uppercase tracking-wider">Recommended by our team</span>
        <h2 className="text-xl md:text-2xl font-black text-[#12263A]">Complete your project</h2>
        <p className="text-sm text-neutral-600 mt-1">Pair with these essentials from {brand} and our workshop to get the job done first time.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {accessories.map(p => {
          const inCart = inCartIds.has(p.id);
          const isAdding = adding === p.id;
          return (
            <div
              key={p.id}
              data-testid={`complete-project-item-${p.id}`}
              className="bg-white border border-neutral-200 hover:border-[#F46A25] rounded-sm overflow-hidden group transition-colors flex flex-col"
            >
              <Link to={`/product/${p.slug || p.id}`} className="block aspect-square overflow-hidden bg-neutral-50">
                <img src={resolveImage(p.image)} alt={p.name} width="400" height="400" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </Link>
              <div className="p-3 flex flex-col flex-1 gap-1">
                <div className="text-[11px] text-neutral-500 font-semibold">{p.brand}</div>
                <Link to={`/product/${p.slug || p.id}`} className="text-sm font-semibold text-[#12263A] hover:text-[#F46A25] line-clamp-2 leading-tight min-h-[2.5rem]">
                  {p.name}
                </Link>
                <div className="text-[#F46A25] font-black text-lg mt-1">£{Number(p.price).toFixed(2)}</div>
                <button
                  onClick={() => handleAdd(p)}
                  disabled={inCart || isAdding}
                  data-testid={`complete-project-add-${p.id}`}
                  className={`mt-1 w-full text-xs font-bold py-2 rounded-sm flex items-center justify-center gap-1 ${
                    inCart
                      ? 'bg-green-50 text-green-700 border border-green-300 cursor-default'
                      : 'bg-[#12263A] hover:bg-[#F46A25] text-white'
                  }`}
                >
                  {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : inCart ? 'In basket' : 'Add to basket'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/** Reviews tab: fetches list of admin-written reviews for this product. */
const ProductReviews = ({ product }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/products/${product.id}/reviews`)
      .then(r => { if (alive) setReviews(r.data); })
      .catch(() => { if (alive) setReviews([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [product.id]);

  return (
    <div data-testid="pdp-reviews-tab">
      <div className="flex items-center gap-4 mb-5 pb-5 border-b border-neutral-200">
        <div className="text-5xl font-black text-[#12263A]">{Number(product.rating || 0).toFixed(1)}</div>
        <div>
          <div className="flex">{[1, 2, 3, 4, 5].map(i => (
            <Star key={i} className={`w-4 h-4 ${i <= Math.round(product.rating || 0) ? 'fill-[#F46A25] text-[#F46A25]' : 'text-neutral-300'}`} />
          ))}</div>
          <div className="text-xs text-neutral-600 mt-1">Based on {product.reviews || 0} review{product.reviews === 1 ? '' : 's'}</div>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#F46A25]" /></div>
      ) : reviews.length === 0 ? (
        <p className="text-neutral-500 italic text-sm">No reviews yet.</p>
      ) : (
        <div className="space-y-4">
          {reviews.map(r => (
            <div key={r.id} className="border-b border-neutral-100 pb-4 last:border-b-0" data-testid={`pdp-review-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <div className="flex">{[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? 'fill-[#F46A25] text-[#F46A25]' : 'text-neutral-300'}`} />
                ))}</div>
                <span className="text-sm font-bold text-[#12263A]">{r.reviewer_name}</span>
                <span className="text-xs text-neutral-400">{new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
              {r.title && <div className="font-bold text-[#12263A] text-sm mb-0.5">{r.title}</div>}
              <p className="text-sm text-neutral-700 whitespace-pre-line leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
